import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import ShiftDaySelection from './FormSteps/ShiftDaySelection';
import ShiftTypeSelection from './FormSteps/ShiftTypeSelection';
import ShiftInformation from '../ShiftInformation/ShiftInformation';

import { Container, Header } from './AddShiftForm.styles';
import { useWeekDays } from '../../utils/Hooks/dates/useWeekDays';
import { selectCurrentVenue } from '../../redux/slices/currentUserSlice';
import { selectAllShiftTypes } from '../../redux/slices/shiftTypesSlice';
import { createNewShift } from '../../redux/shifts/shiftsThunks';

dayjs.extend(isToday);

const DEFAULT_SHIFT_DURATION = 1; // hours

const AddShiftForm = ({ onClose, selectedDate, dateFromShiftButton, handleSelectedDate, preSelectedRole }) => {
  // If we have a pre-selected role, start at customization step after creating the shift
  const initialStep = preSelectedRole
    ? 'shiftCustomization'
    : dateFromShiftButton
      ? 'shiftTypeSelection'
      : 'shiftDaySelection';
  const [step, setStep] = useState(initialStep);
  const [day, setDay] = useState(dateFromShiftButton ? new Date(dateFromShiftButton) : null);
  const [createdShift, setCreatedShift] = useState(null);
  const [isNewShift, setIsNewShift] = useState(false);

  const dispatch = useDispatch();

  // Get the current venue ID
  const venueId = useSelector(selectCurrentVenue)?.id;

  // Get shift types to find the matching object for preSelectedRole
  const shiftTypes = useSelector(selectAllShiftTypes);

  // get the current week days
  const { weekDays: currentWeek } = useWeekDays(selectedDate);

  const hasCreatedShiftRef = useRef(false);

  // Event handlers
  const handleDaySelect = useCallback((selectedDay) => {
    setDay(selectedDay);
    setStep('shiftTypeSelection');
  }, []);

  const handleTypeSelect = useCallback(
    async (selectedType) => {
      const selectedDayJs = dayjs(day);
      const now = dayjs();

      // Set start time to either next hour if today, or 8 AM if future date
      const start = selectedDayJs.isToday()
        ? now.add(1, 'hour').startOf('hour')
        : selectedDayJs.hour(8).startOf('hour');

      const end = start.add(DEFAULT_SHIFT_DURATION, 'hour');

      // Build the shift data
      const shiftData = {
        createdAt: now.valueOf(),
        type: selectedType,
        start: start.toDate(),
        end: end.toDate(),
        projectedCost: selectedType?.defaultCost
          ? Math.round(end.diff(start, 'hour', true) * selectedType.defaultCost)
          : 0,
        vid: venueId,
      };

      try {
        const resultAction = await dispatch(createNewShift(shiftData));
        const newShift = resultAction.payload;

        // Saves the new shift and sets the isNewShift flag
        setIsNewShift(true);
        setCreatedShift(newShift);
        handleSelectedDate(shiftData.start);

        // Moves to the next step
        setStep('shiftCustomization');
      } catch (error) {
        console.error('Error creating a shift:', error);
      }
    },
    [day, venueId, dispatch, handleSelectedDate]
  );

  // if the date is coming from the shift button, select the day and move to the next step
  useEffect(() => {
    if (dateFromShiftButton) {
      handleDaySelect(dateFromShiftButton);
    }
  }, [dateFromShiftButton, handleDaySelect]);

  // Create shift immediately if we have a pre-selected role
  useEffect(() => {
    if (preSelectedRole && shiftTypes.length > 0 && !hasCreatedShiftRef.current) {
      const matchingShiftType = shiftTypes.find((type) => type.name === preSelectedRole.name);
      if (matchingShiftType) {
        hasCreatedShiftRef.current = true;
        handleTypeSelect(matchingShiftType);
      }
    }
  }, [preSelectedRole, shiftTypes, handleTypeSelect]);

  return (
    <Container>
      <Header>
        {step === 'shiftDaySelection' && <ShiftDaySelection onSelect={handleDaySelect} currentWeek={currentWeek} />}
        {step === 'shiftTypeSelection' && !preSelectedRole && <ShiftTypeSelection onSelect={handleTypeSelect} />}
        {step === 'shiftCustomization' && createdShift && (
          <ShiftInformation isNewShift={isNewShift} shift={createdShift} handleCloseDrawer={onClose} />
        )}
      </Header>
    </Container>
  );
};

AddShiftForm.propTypes = {
  onClose: PropTypes.func,
  selectedDate: PropTypes.instanceOf(Date),
  dateFromShiftButton: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  handleSelectedDate: PropTypes.func,
  preSelectedRole: PropTypes.object,
};

export default AddShiftForm;
